import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import {
  Table,
  Container,
  Row,
  Col,
  Button as BootstrapButton,
} from "react-bootstrap";
import { IoAdd } from "react-icons/io5";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import TopbarAction from "../../components/layout/topbar/TopbarAction";
import filterIcon from "../../media/filter_icon.png";
import EditableModal from "../../components/layout/editableModal/EditableModal";
import { getLocale } from "../../i18n/config";
import { getGroupTypeLabel } from "../../i18n/groupTypes";
import { useLanguage } from "../../hooks/useLanguage";
import { useUserSettings } from "../../context/UserSettingsContext";
import {
  getLocalizedServiceField,
  getServiceDisplayField,
  localizedFieldKey,
} from "../../i18n/localizedService";

// Значения (value) — доменные, уходят на сервер; label — только для отображения.
const TYPE_VALUES = [
  { value: "procedure", labelKey: "types.procedure" },
  { value: "procedure_extra", labelKey: "types.procedureExtra" },
  { value: "therapy", labelKey: "types.therapy" },
  { value: "diagnostic", labelKey: "types.diagnostic" },
  { value: "consultation", labelKey: "types.consultation" },
  { value: "peptides", labelKey: "types.peptides" },
  { value: "manipulations", labelKey: "types.manipulations" },
];

const TYPE_LABEL_TO_KEY = TYPE_VALUES.reduce((acc, option) => {
  acc[option.value] = option.value;
  return acc;
}, {});

const STATUS_VALUES = [
  { value: "В наличии", labelKey: "statuses.inStock" },
  { value: "Ограничено", labelKey: "statuses.limited" },
  { value: "Нет в наличии", labelKey: "statuses.outOfStock" },
];

const EMPTY_SERVICE_FORM = {
  name: "",
  type: "procedure",
  status: "В наличии",
  price: "",
  composition: "",
  technology: "",
  filials: [],
};

const SERVICE_FIELDS = (t, typeOptions, statusOptions, filialOptions = null) => {
  const fields = [
    { name: "name", label: t("fields.name"), required: true, bold: true },
    { name: "price", label: t("fields.price"), type: "number", bold: true },
    {
      name: "type",
      label: t("fields.category"),
      type: "select",
      options: typeOptions,
      required: true,
    },
    {
      name: "status",
      label: t("fields.status"),
      type: "select",
      options: statusOptions,
    },
    {
      name: "composition",
      label: t("fields.composition"),
      type: "textarea",
      rows: 2,
    },
    {
      name: "technology",
      label: t("fields.technology"),
      type: "textarea",
      rows: 4,
    },
  ];
  if (filialOptions) {
    fields.push({
      name: "filials",
      label: t("fields.filials"),
      type: "checkboxes",
      options: filialOptions,
      hint: t("fields.filialsHint"),
    });
  }
  return fields;
};

const unwrapField = (field, fallback = "") => {
  if (field && typeof field === "object" && "value" in field) {
    return field.value ?? fallback;
  }
  return field ?? fallback;
};

const normalizeType = (type) => TYPE_LABEL_TO_KEY[type] || type || "procedure";

/** Возвращает ключ статуса (для перевода и CSS) по доменному значению. */
const statusKey = (status) => {
  if (status === "active" || status === "В наличии") return "inStock";
  if (status === "limited" || status === "Ограничено") return "limited";
  if (
    status === "inactive" ||
    status === "Нет в наличии" ||
    status === "Не в наличии"
  ) {
    return "outOfStock";
  }
  return null;
};

/** Приводит доменное значение статуса к каноничному (уходит на сервер). */
const normalizeStatusValue = (status) => {
  const key = statusKey(status);
  const found = STATUS_VALUES.find((o) => o.labelKey === `statuses.${key}`);
  return found ? found.value : status || STATUS_VALUES[0].value;
};

/**
 * Приводит филиал (строку `clinics`) к {id, name}.
 *
 * Защита от чужого формата ответа: если сервер не знает action=get_filials
 * (например, служба не перезапущена), он отдаёт список услуг в формате
 * {value, access}. Такие записи отбрасываем — иначе React падает
 * с "Objects are not valid as a React child".
 */
const normalizeClinic = (clinic) => {
  if (!clinic || typeof clinic !== "object") return null;
  const rawId = clinic.id;
  const rawName = clinic.name;
  if (typeof rawId === "object" || (rawName != null && typeof rawName === "object")) {
    return null;
  }
  const id = Number(rawId) || 0;
  if (!id) return null;
  return { id, name: typeof rawName === "string" ? rawName : "" };
};

function Products() {
  const { t, i18n } = useTranslation("products");
  const { language } = useLanguage();
  const locale = getLocale(i18n.language);
  const typeOptions = useMemo(
    () => TYPE_VALUES.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const statusOptions = useMemo(
    () => STATUS_VALUES.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { get, update } = useUserSettings();
  const [clinics, setClinics] = useState([]);
  const [filters, setFilters] = useState(() => ({
    type: "all",
    branch: String(get("filters.products.clinic_id", "") || ""),
  }));

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formInitialData, setFormInitialData] = useState(EMPTY_SERVICE_FORM);

  // Опции филиалов для карточки услуги (пустой список = доступна везде).
  const filialOptions = useMemo(
    () =>
      clinics.map((clinic) => ({
        value: String(clinic.id),
        label: clinic.name || t("list.branchFallback", { id: clinic.id }),
      })),
    [clinics, t]
  );

  const serviceFields = useMemo(
    () =>
      SERVICE_FIELDS(
        t,
        typeOptions,
        statusOptions,
        editingId ? filialOptions : null
      ),
    [t, typeOptions, statusOptions, editingId, filialOptions]
  );
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  const loadServices = async () => {
    const preserveSearchFocus = captureSearchFocus();
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "products",
        block: "list",
        lang: language,
        type: filters.type === "all" ? "" : filters.type,
        clinic_id: filters.branch ? Number(filters.branch) : undefined,
        search: debouncedSearchTerm.trim(),
        page: pagination.page,
        limit: pagination.limit,
      });
      setServices(data.result || []);
      if (data.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: data.pagination.page ?? prev.page,
          limit: data.pagination.limit ?? prev.limit,
          total: data.pagination.total ?? prev.total,
          pages: data.pagination.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      console.error("Ошибка загрузки услуг:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  };

  useEffect(() => {
    loadServices();
    // language в зависимостях: на en/ar поиск на бэке идёт по name_en.
  }, [filters.type, filters.branch, debouncedSearchTerm, pagination.page, language]);

  // Филиалы — та же таблица `clinics`, что и на странице «Клиники»
  // (модуль contacts/list). Он уже развёрнут, поэтому список приходит даже
  // без перезапуска службы после правок products/list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await GetData({
          module: "contacts",
          block: "list",
        });
        if (!cancelled) {
          const list = Array.isArray(data.result) ? data.result : [];
          setClinics(list.map(normalizeClinic).filter(Boolean));
        }
      } catch (err) {
        console.error("Ошибка загрузки филиалов:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = t("list.title");
  }, [t]);

  const handleAddClick = () => {
    setEditingId(null);
    setFormInitialData({ ...EMPTY_SERVICE_FORM });
    setShowModal(true);
  };

  const handleEditClick = async (service) => {
    const id = unwrapField(service?.id);
    if (!id) return;

    const openWithData = (serviceData) => {
      setEditingId(Number(id));
      setFormInitialData({
        name: getLocalizedServiceField(serviceData, "name", language),
        type: normalizeType(unwrapField(serviceData.type)),
        status: normalizeStatusValue(unwrapField(serviceData.status)),
        price: String(unwrapField(serviceData.price, "")),
        composition: getLocalizedServiceField(serviceData, "composition", language),
        technology: getLocalizedServiceField(serviceData, "technology", language),
        filials: unwrapField(serviceData.filials, []) || [],
      });
      setShowModal(true);
    };

    try {
      const data = await GetData({
        module: "products",
        block: "info",
        id_services: id,
      });
      let serviceData = data.result || service;
      if (Array.isArray(serviceData) && serviceData.length > 0) {
        serviceData = serviceData[0];
      }
      openWithData(serviceData);
    } catch (err) {
      console.error("Ошибка загрузки услуги:", err);
      openWithData(service);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSaveService = async (formData) => {
    try {
      if (editingId) {
        await GetData({
          module: "products",
          block: "info",
          id_services: editingId,
          action: "change",
          data: {
            [localizedFieldKey("name", language)]: { value: formData.name },
            [localizedFieldKey("composition", language)]: { value: formData.composition },
            price: { value: Number(formData.price) || 0 },
            type: { value: formData.type },
            status: { value: formData.status },
            [localizedFieldKey("technology", language)]: { value: formData.technology },
            filials: {
              value: (Array.isArray(formData.filials) ? formData.filials : []).map(
                (id) => Number(id)
              ),
            },
          },
        });
      } else {
        await GetData({
          module: "products",
          block: "list",
          action: "add",
          lang: language,
          [localizedFieldKey("name", language)]: formData.name,
          type: formData.type,
          price: Number(formData.price) || 0,
          [localizedFieldKey("composition", language)]: formData.composition || "",
          [localizedFieldKey("technology", language)]: formData.technology || "",
          status: formData.status || STATUS_VALUES[0].value,
        });
      }
      await loadServices();
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      alert(t("list.errors.saveFailed"));
      return false;
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (filterType === "branch") {
      update({ filters: { products: { clinic_id: value } } });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters((prev) => ({ ...prev, type: "all", branch: "" }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    update({ filters: { products: { clinic_id: "" } } });
  };

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const renderPageButtons = () => {
    if (totalPages <= 1) return null;
    if (totalPages < 5) {
      return Array.from({ length: totalPages }, (_, idx) => {
        const pageNum = idx + 1;
        return (
          <BootstrapButton
            key={pageNum}
            variant={pageNum === pagination.page ? "primary" : "outline-secondary"}
            size="sm"
            className="mx-1"
            onClick={() => handlePageClick(pageNum)}>
            {pageNum}
          </BootstrapButton>
        );
      });
    }
    const currentPage = pagination.page;
    let start = Math.max(1, currentPage - 1);
    let end = start + 2;
    if (end > totalPages) {
      end = totalPages;
      start = end - 2;
    }
    const buttons = [];
    if (start > 1) {
      buttons.push(
        <BootstrapButton
          key="left-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(start - 1)}>
          ...
        </BootstrapButton>
      );
    }
    for (let p = start; p <= end; p++) {
      buttons.push(
        <BootstrapButton
          key={p}
          variant={p === currentPage ? "primary" : "outline-secondary"}
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(p)}>
          {p}
        </BootstrapButton>
      );
    }
    if (end < totalPages) {
      buttons.push(
        <BootstrapButton
          key="right-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(end + 1)}>
          ...
        </BootstrapButton>
      );
    }
    return buttons;
  };

  const getStatusLabel = (status) => {
    const key = statusKey(status);
    return key ? t(`statuses.${key}`) : status;
  };

  const getStatusClass = (status) => {
    const key = statusKey(status);
    if (key === "inStock") return "status-active";
    if (key === "limited") return "status-limited";
    return "status-inactive";
  };

  return (
    <Container fluid="md">
      {/* Кнопка «Добавить» живёт в шапке страницы (общий верхний блок) */}
      <TopbarAction>
        <button
          type="button"
          className="topbar-action"
          onClick={handleAddClick}>
          <span className="topbar-action__icon" aria-hidden="true">
            <IoAdd size={16} />
          </span>
          <span className="topbar-action__label">{t("list.add")}</span>
        </button>
      </TopbarAction>

      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <select
                        className={
                          filters.type === "all" ? "is-placeholder" : ""
                        }
                        value={filters.type}
                        onChange={(e) =>
                          handleFilterChange("type", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("list.categoryLabel"))}
                        </option>
                        {typeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <select
                        className={
                          filters.branch === "" || filters.branch === "all"
                            ? "is-placeholder"
                            : ""
                        }
                        value={filters.branch}
                        onChange={(e) =>
                          handleFilterChange("branch", e.target.value)
                        }
                      >
                        <option value="">
                          {asPlaceholder(t("list.branchLabel"))}
                        </option>
                        {clinics.map((clinic) => (
                          <option key={clinic.id} value={String(clinic.id)}>
                            {clinic.name || t("list.branchFallback", { id: clinic.id })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="button-default light"
                      onClick={clearFilters}
                    >
                      {t("filters.reset", { ns: "common" })}
                    </button>
                  </div>
                </div>
              </div>

              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : services.length === 0 ? (
                  <div className="no-patients">
                    {t("list.notFound")}
                  </div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colName")}</th>
                        <th>{t("list.colCategory")}</th>
                        <th>{t("list.colPrice")}</th>
                        <th>{t("list.colStatus")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service) => {
                        const statusValue = unwrapField(service?.status);
                        return (
                          <tr
                            key={unwrapField(service?.id) || service?.id}
                            className="table-item-link"
                            onClick={() => handleEditClick(service)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <div className="table-item-name">
                                {getServiceDisplayField(service, "name", language)}
                              </div>
                            </td>
                            <td>{getGroupTypeLabel(t, { name: unwrapField(service?.type) })}</td>
                            <td>
                              {Number(unwrapField(service?.price, 0)).toLocaleString(
                                locale,
                              ) + " " + t("priceSuffix")}
                            </td>
                            <td>
                              <span className={`status-badge ${getStatusClass(statusValue)}`}>
                                {getStatusLabel(statusValue)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="table-summary">
                        <td colSpan={4} className="summary-cell">
                          {t("list.found", { count: pagination.total || services.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {services.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("pageOf", { ns: "common", page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <EditableModal
        show={showModal}
        onHide={handleModalClose}
        title={editingId ? t("list.editTitle") : t("list.addTitle")}
        initialData={formInitialData}
        fields={serviceFields}
        onSave={handleSaveService}
      />
    </Container>
  );
}

export default Products;
